import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type {
    FloatingPanelPosition,
    FloatingPanelRect,
    FloatingPanelSize,
} from 'src/components/svelte/ui/floating-panel/types';
import type { SyncStorageService } from 'src/services/SyncStorageService';
import { z } from 'zod';

/**
 * Position is stored as a fraction of the viewport so a panel keeps its relative
 * placement when the window size changes; size is stored in pixels.
 */
const storedPanelRectSchema = z.object({
    position: z.object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
    }),
    size: z.object({
        width: z.number().positive(),
        height: z.number().positive(),
    }),
});

type StoredPanelRect = z.infer<typeof storedPanelRectSchema>;

const storedPanelRectJson = z.codec(z.string(), storedPanelRectSchema, {
    decode: (json, ctx) => {
        try {
            return JSON.parse(json);
        } catch (error) {
            ctx.issues.push({ code: 'invalid_format', format: 'json', input: json, message: (error as Error).message });
            return z.NEVER;
        }
    },
    encode: (rect) => JSON.stringify(rect),
});

type PanelRectStorage = Pick<SyncStorageService, 'getItem' | 'setItem' | 'removeItem'>;

export interface PersistedPanelRectOptions {
    storage: PanelRectStorage;
    log: Logger;
    storageKey: string;
    defaultSize: FloatingPanelSize;
    minSize?: FloatingPanelSize;
}

/** Spread onto a `FloatingPanel` to restore its last position and size and save each change. */
export interface PersistedPanelRectProps {
    defaultSize: FloatingPanelSize;
    /** Only set when a rect was restored, so a panel with nothing saved still centers. */
    defaultPosition?: FloatingPanelPosition;
    minSize?: FloatingPanelSize;
    onRectChangeEnd: (rect: FloatingPanelRect) => void;
}

function getViewport(): FloatingPanelSize {
    return { width: window.innerWidth, height: window.innerHeight };
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

/** Fit a stored rect into the current viewport: the machine does not clamp a `defaultPosition` on open. */
function restoreRect(stored: StoredPanelRect, viewport: FloatingPanelSize, minSize?: FloatingPanelSize) {
    const size = {
        width: clamp(stored.size.width, minSize?.width ?? 0, viewport.width),
        height: clamp(stored.size.height, minSize?.height ?? 0, viewport.height),
    };
    const position = {
        x: clamp(stored.position.x * viewport.width, 0, Math.max(0, viewport.width - size.width)),
        y: clamp(stored.position.y * viewport.height, 0, Math.max(0, viewport.height - size.height)),
    };
    return { position, size };
}

function readStoredRect({ storage, log, storageKey }: PersistedPanelRectOptions): StoredPanelRect | undefined {
    try {
        const raw = storage.getItem(storageKey);
        if (!raw) return undefined;
        const decoded = storedPanelRectJson.safeDecode(raw);
        if (decoded.success) return decoded.data;
        log.warn({
            message: `Discarding malformed persisted panel rect for ${storageKey}`,
            storageKey,
            issues: decoded.error.issues,
        });
        storage.removeItem(storageKey);
        return undefined;
    } catch (error) {
        log.warn({
            message: `Failed to read persisted panel rect for ${storageKey}`,
            error: error as Error,
            storageKey,
        });
        return undefined;
    }
}

function writeStoredRect({ storage, log, storageKey }: PersistedPanelRectOptions, rect: FloatingPanelRect): void {
    const viewport = getViewport();
    const stored: StoredPanelRect = {
        position: {
            x: clamp(rect.position.x / viewport.width, 0, 1),
            y: clamp(rect.position.y / viewport.height, 0, 1),
        },
        size: rect.size,
    };
    try {
        storage.setItem(storageKey, storedPanelRectJson.encode(stored));
    } catch (error) {
        log.warn({ message: `Failed to persist panel rect for ${storageKey}`, error: error as Error, storageKey });
    }
}

/**
 * Read a floating panel's last rect from storage and return the props that
 * restore it and save later drags and resizes. Corrupt data is removed so the
 * next load starts clean.
 */
export function createPersistedPanelRect(options: PersistedPanelRectOptions): PersistedPanelRectProps {
    const { defaultSize, minSize } = options;
    const onRectChangeEnd = (rect: FloatingPanelRect) => writeStoredRect(options, rect);
    const stored = readStoredRect(options);
    if (!stored) return { defaultSize, minSize, onRectChangeEnd };

    const restored = restoreRect(stored, getViewport(), minSize);
    return { defaultSize: restored.size, defaultPosition: restored.position, minSize, onRectChangeEnd };
}
